/**
 * CL-BUDGET-001C-A1 — Selective budget permission editor (pure).
 *
 * Maps plain-language budget controls to canonical staff financial keys:
 * canViewBudget, canManageBudget (with canAccessFinancials parent).
 */

import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import {
  STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS,
  STAFF_FINANCIAL_VIEW_PERMISSION_KEYS,
} from "./projectStaffFinancialPermissionsModel";
export const FULL_BUDGET_ACCESS_CONTROL_KEY = "fullBudgetAccess";

export const BUDGET_PERMISSION_CONTROL_KEYS = Object.freeze([
  "canViewBudget",
  "canManageBudget",
]);

export const BUDGET_PERMISSION_CONTROL_LABELS = Object.freeze({
  [FULL_BUDGET_ACCESS_CONTROL_KEY]: "Full budget access",
  canViewBudget: "View budget",
  canManageBudget: "Edit budget",
});

const BUDGET_CHILD_PARENTS = Object.freeze([
  { child: "canViewBudget", parents: ["canAccessFinancials"] },
  { child: "canManageBudget", parents: ["canAccessFinancials", "canViewBudget"] },
]);

const BUDGET_CONTROL_DEPENDENTS = Object.freeze({
  canViewBudget: ["canManageBudget"],
});

function clonePermissions(permissions = {}) {
  return { ...(permissions || {}) };
}

function budgetControlEnabled(permissions = {}, key = "") {
  return permissions?.[key] === true;
}

export function isFullBudgetAccessEnabled(permissions = {}) {
  return BUDGET_PERMISSION_CONTROL_KEYS.every((key) => budgetControlEnabled(permissions, key));
}

function parentsRequiredByEnabledBudgetControls(permissions = {}) {
  const required = new Set();
  BUDGET_CHILD_PARENTS.forEach(({ child, parents }) => {
    if (!budgetControlEnabled(permissions, child)) return;
    required.add(child);
    parents.forEach((parent) => required.add(parent));
  });
  return required;
}

function nonBudgetReasonToKeepParent(permissions = {}, parentKey = "") {
  if (parentKey !== "canAccessFinancials") return false;
  const otherFinancialViewKeys = STAFF_FINANCIAL_VIEW_PERMISSION_KEYS.filter(
    (key) => key !== "canViewBudget"
  );
  const otherFinancialManageKeys = STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS.filter(
    (key) => key !== "canManageBudget"
  );
  return (
    otherFinancialViewKeys.some((key) => budgetControlEnabled(permissions, key)) ||
    otherFinancialManageKeys.some((key) => budgetControlEnabled(permissions, key))
  );
}

export function normalizeBudgetPermissionDependencies(permissions = {}) {
  const next = clonePermissions(permissions);
  let changed = true;
  while (changed) {
    changed = false;
    BUDGET_CHILD_PARENTS.forEach(({ child, parents }) => {
      if (!budgetControlEnabled(next, child)) return;
      next[child] = true;
      parents.forEach((parent) => {
        if (next[parent] !== true) {
          next[parent] = true;
          changed = true;
        }
      });
    });
  }
  return next;
}

export function pruneBudgetPermissionDependencies(permissions = {}) {
  const next = clonePermissions(permissions);
  const required = parentsRequiredByEnabledBudgetControls(next);

  ["canAccessFinancials", ...BUDGET_PERMISSION_CONTROL_KEYS].forEach((key) => {
    if (BUDGET_PERMISSION_CONTROL_KEYS.includes(key)) {
      if (!required.has(key) && typeof next[key] === "boolean") {
        next[key] = false;
      }
      return;
    }
    if (required.has(key)) {
      next[key] = true;
      return;
    }
    if (nonBudgetReasonToKeepParent(next, key)) {
      return;
    }
    if (typeof next[key] === "boolean") {
      next[key] = false;
    }
  });

  return next;
}

function disableBudgetControlDependents(permissions = {}, key = "") {
  const next = clonePermissions(permissions);
  const queue = [...(BUDGET_CONTROL_DEPENDENTS[key] || [])];
  next[key] = false;
  while (queue.length) {
    const dependent = queue.shift();
    if (next[dependent] !== true) continue;
    next[dependent] = false;
    queue.push(...(BUDGET_CONTROL_DEPENDENTS[dependent] || []));
  }
  return next;
}

export function applyBudgetPermissionToggle(permissions = {}, key = "", checked = false) {
  if (key === FULL_BUDGET_ACCESS_CONTROL_KEY) {
    const next = clonePermissions(permissions);
    BUDGET_PERMISSION_CONTROL_KEYS.forEach((controlKey) => {
      next[controlKey] = checked === true;
    });
    return checked ? normalizeBudgetPermissionDependencies(next) : pruneBudgetPermissionDependencies(next);
  }

  if (checked === true) {
    return normalizeBudgetPermissionDependencies({
      ...permissions,
      [key]: true,
    });
  }

  const withoutDependents = disableBudgetControlDependents(permissions, key);
  return pruneBudgetPermissionDependencies(withoutDependents);
}

export function listProjectBudgetPermissionControls() {
  return BUDGET_PERMISSION_CONTROL_KEYS.map((key) => ({
    key,
    label: BUDGET_PERMISSION_CONTROL_LABELS[key],
  }));
}

export function isBudgetPermissionControlChecked(key, permissions = {}) {
  if (key === FULL_BUDGET_ACCESS_CONTROL_KEY) {
    return isFullBudgetAccessEnabled(permissions);
  }
  return budgetControlEnabled(permissions, key);
}

export function mergeBudgetPermissionsPreservingUnrelated(existing = {}, patch = {}) {
  return {
    ...existing,
    ...patch,
  };
}

export function applyDeliberateBudgetPermissionEdit({
  permissions = {},
  key = "",
  checked = false,
} = {}) {
  const toggled = applyBudgetPermissionToggle(permissions, key, checked);
  return mergeBudgetPermissionsPreservingUnrelated(permissions, toggled);
}

function pickPersistedBudgetPermissionFlags(raw = {}) {
  const flags = {};
  if (typeof raw?.canAccessFinancials === "boolean") {
    flags.canAccessFinancials = raw.canAccessFinancials;
  }
  BUDGET_PERMISSION_CONTROL_KEYS.forEach((key) => {
    if (typeof raw?.[key] === "boolean") {
      flags[key] = raw[key];
    }
  });
  return flags;
}

export function normalizeBudgetPermissionsForAssignmentSave(
  permissions = {},
  { accountingPermissionsUiEnabled = null, rawPermissions = null } = {}
) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return { ...permissions };
  }
  const source = rawPermissions || permissions;
  let next = {
    ...clonePermissions(permissions),
    ...pickPersistedBudgetPermissionFlags(source),
  };
  next = pruneBudgetPermissionDependencies(normalizeBudgetPermissionDependencies(next));
  const budgetPatch = {};
  ["canAccessFinancials", ...BUDGET_PERMISSION_CONTROL_KEYS].forEach((key) => {
    if (typeof next[key] === "boolean") {
      budgetPatch[key] = next[key];
    }
  });
  return mergeBudgetPermissionsPreservingUnrelated(permissions, budgetPatch);
}
